"use client";

import { useLayoutEffect } from "react";
import { usePreviousPageStore } from "@/store/previousPageStore";
import { usePathname } from "next/navigation";

// Re-export for convenience — consumers don't need to know about the store
export { getPreviousPage } from "@/store/previousPageStore";

/**
 * Mount this hook once in AppShell to track route changes.
 * Uses a Zustand store internally so the state is:
 * - SSR-safe (store is created per client)
 * - Strict Mode safe (Zustand handles double effects)
 * - Accessible outside React via `getPreviousPage()`
 *
 * Uses useLayoutEffect (not useEffect) so the store is updated during the
 * commit phase, before paint and before any child useEffect callbacks.
 * React fires all useLayoutEffects across the tree before any useEffect,
 * so child page-view events (which use useEffect) will read the correct
 * previousPage. A plain useEffect here would fire AFTER child useEffects
 * (bottom-up ordering), causing previousPage to lag by one navigation.
 */
export function usePreviousPageTracker() {
  const pathname = usePathname();
  const onRouteChange = usePreviousPageStore((s) => s.onRouteChange);

  useLayoutEffect(() => {
    if (pathname) {
      onRouteChange(pathname);
    }
  }, [pathname, onRouteChange]);
}
